import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { isValidUrl } from '../../../../../shared/utils/url';
import { window } from '../../../../globals';
import LabeledMultiTextInput from '../../../generic/Forms/LabeledMultiTextInput';
import { icons } from '../../../generic/Icon';
import TooltipIcon from '../../../generic/TooltipIcon';
import Section from '../../../shared/settings/SettingsPageSection';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { HiddenSnippetUrlsSettings } from '../../../../../shared/settings/project/hiddenSnippetUrls';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';

import { PropagateExcludedSnippetRepositoriesModal } from './PropagateExcludedSnippetRepositoriesModal';

const MAX_HIDDEN_SNIPPET_URLS = 100;
const MAX_HIDDEN_SNIPPET_URL_LENGTH = 2048;

/**
 * Organization-default version of the project "Excluded Snippets Repositories" setting.
 * Edits the org default list, saves it, and can propagate it to every project.
 *
 * Stored/sent as URLs (generic); surfaced to users as "Repositories".
 */
export const ExcludedSnippetRepositoriesSection: VoidFunctionComponent = () => {
  const [invalidUrls, setInvalidUrls] = useState<string[]>([]);
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const endpoint: OrganizationSettingsEndpoint =
    `/api/organizations/${window.user?.organizationId ?? 0}/settings/projects/hidden-snippet-urls`;
  const { isDirty, onUpdateSettings, settings, isLoading, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<HiddenSnippetUrlsSettings>({
      endpoint,
      settingsSection: 'project hidden snippet urls',
    });

  return (
    <>
      <Section>
        <Section.Left
          heading={(
            <>
              Excluded Snippets Repositories
              <TooltipIcon
                id="org-excluded-snippet-repositories-help"
                icon={icons.questionCircle}
                tooltip="Available for Licensing and Security scans only"
                showTooltip
              />
            </>
          )}
        >
          <Section.Paragraph>
            Add GitHub repository URLs to prevent snippet matches from those sources by default.
          </Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton isDisabled={isDirty} onClick={() => setIsPropagateModalOpen(true)} />
            <Section.SaveButton
              onClick={onSaveSettings}
              isLoading={isLoading.save}
              isDisabled={isSaveButtonDisabled}
            />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <LabeledMultiTextInput
            id="org-settings-hidden-snippet-urls"
            label="URL"
            placeholder="Type a repository URL and hit Enter"
            values={settings?.projectDefaultHiddenSnippetUrls ?? []}
            onChange={(projectDefaultHiddenSnippetUrls) => onUpdateSettings({ projectDefaultHiddenSnippetUrls })}
            isDisabled={isLoading.any}
            maxItems={MAX_HIDDEN_SNIPPET_URLS}
            maxLength={MAX_HIDDEN_SNIPPET_URL_LENGTH}
            validate={isValidUrl}
            onInvalidItems={setInvalidUrls}
            size="small"
          />
          {invalidUrls.length > 0 && (
            <p className="text-danger">
              {invalidUrls.length === 1
                ? `"${invalidUrls[0]}" is not a valid URL.`
                : `${invalidUrls.length} entries were skipped because they are not valid URLs: ${invalidUrls.join(', ')}`}
            </p>
          )}
        </Section.Right>
      </Section>
      <PropagateExcludedSnippetRepositoriesModal
        isOpen={isPropagateModalOpen}
        onHide={() => setIsPropagateModalOpen(false)}
        endpoint={endpoint}
      />
    </>
  );
};
